import { Quote, Star } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { Reveal } from '@/shared/motion/reveal'
import { Section, SectionHeader } from '@/shared/ui/section'
import { defineT, useTranslation } from '@/shared/i18n/use-translation'
import { cn } from '@/shared/lib/cn'
import { api } from '@/shared/lib/api-client'

const T = defineT({
  eyebrow: { ar: 'آراء طلابي', en: 'Student stories' },
  title: { ar: 'بنبني سوا', en: 'We build together' },
  description: {
    ar: 'أكثر من 1500 طالب اتعلموا منّي الـ AI، البرمجة، والتصميم. دي بعض من قصصهم.',
    en: 'Over 1,500 students have learned AI, code, and design with me. Here are some of their stories.',
  },
})

interface ApiTestimonial {
  id: string
  studentName: string
  studentImage?: string | null
  studentTitle?: string | null
  content: string
  rating: number
  isFeatured: boolean
  isActive: boolean
  sortOrder: number
}

// Fallback data — shown when the API is offline
const FALLBACK: ApiTestimonial[] = [
  {
    id: '1',
    studentName: 'Ahmed Hassan',
    studentTitle: 'Graphic Designer',
    content:
      "Abdallah's course changed how I work. I now use AI in every step of my daily process and saved enormous time.",
    rating: 5,
    isFeatured: true,
    isActive: true,
    sortOrder: 1,
  },
  {
    id: '2',
    studentName: 'Sarah Mahmoud',
    studentTitle: 'Content Creator',
    content:
      'The best Arabic-language AI instructor. Clear explanations, hands-on projects, and real follow-up.',
    rating: 5,
    isFeatured: true,
    isActive: true,
    sortOrder: 2,
  },
  {
    id: '3',
    studentName: 'Omar Khaled',
    studentTitle: 'Video Editor',
    content:
      'A week after finishing the video course I shipped my first AI-driven project. Highly practical.',
    rating: 5,
    isFeatured: false,
    isActive: true,
    sortOrder: 3,
  },
  {
    id: '4',
    studentName: 'Mona Adel',
    studentTitle: 'Brand Owner',
    content:
      'The prompt library and tools hub saved me hundreds of hours of trial-and-error. A real treasure.',
    rating: 5,
    isFeatured: false,
    isActive: true,
    sortOrder: 4,
  },
]

export function Testimonials() {
  const { t } = useTranslation(T)

  const { data } = useQuery({
    queryKey: ['testimonials', 'public'],
    queryFn: async () => {
      const res = await api.get<ApiTestimonial[]>('/testimonials')
      if (!res.success) return FALLBACK
      return res.data.filter((t) => t.isActive)
    },
    staleTime: 30 * 60 * 1000,
    placeholderData: FALLBACK,
  })

  const testimonials = (data ?? FALLBACK).slice(0, 4)

  return (
    <Section id="testimonials" pad="md">
      <Reveal>
        <SectionHeader
          eyebrow={t('eyebrow')}
          title={t('title')}
          description={t('description')}
        />
      </Reveal>

      <div className="stagger grid gap-5 md:grid-cols-2">
        {testimonials.map((tm) => (
          <Reveal key={tm.id} className="contents">
            <figure
              className={cn(
                'relative isolate flex flex-col rounded-2xl border border-border bg-card p-7',
                'card-hover',
              )}
            >
              <Quote
                className="absolute end-6 top-6 h-8 w-8 text-primary/20"
                aria-hidden
              />
              <div
                className="flex items-center gap-1 text-primary"
                aria-label={`${tm.rating} of 5`}
              >
                {Array.from({ length: Math.min(tm.rating, 5) }).map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-current" aria-hidden />
                ))}
              </div>
              <blockquote className="mt-5 text-base leading-relaxed text-foreground/90">
                "{tm.content}"
              </blockquote>
              <figcaption className="mt-6 flex items-center gap-3 border-t border-border pt-5">
                {tm.studentImage ? (
                  <img
                    src={tm.studentImage}
                    alt={tm.studentName}
                    width={40}
                    height={40}
                    loading="lazy"
                    decoding="async"
                    className="h-10 w-10 rounded-full object-cover ring-2 ring-primary/20"
                  />
                ) : (
                  <span className="grid h-10 w-10 place-items-center rounded-full bg-gradient-to-br from-primary to-primary-700 font-display text-sm font-bold text-primary-foreground">
                    {tm.studentName.charAt(0)}
                  </span>
                )}
                <div>
                  <p className="font-display text-sm font-bold">{tm.studentName}</p>
                  {tm.studentTitle && (
                    <p className="text-xs text-muted-foreground">{tm.studentTitle}</p>
                  )}
                </div>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </Section>
  )
}
